import React, { useState, useEffect } from 'react';
import { BusinessModel } from '../types/template';
import { TemplateService } from '../services/templateService';
import './BusinessModelSelectionModal.css';

interface BusinessModelSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectBusinessModel: (businessModel: BusinessModel, categoryId?: string, transportationMethod?: string) => void;
  currentUserRole?: string;
  onEditBusinessModel?: (businessModel: BusinessModel) => void;
}

const BusinessModelSelectionModal: React.FC<BusinessModelSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectBusinessModel,
  currentUserRole,
  onEditBusinessModel
}) => {
  const [businessModels, setBusinessModels] = useState<BusinessModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadBusinessModels();
    }
  }, [isOpen]);

  const loadBusinessModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const models = await TemplateService.getAllBusinessModels();
      setBusinessModels(models);
    } catch (err) {
      console.error('Error loading business models:', err);
      setError('Failed to load business models');
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessModelSelect = (businessModel: BusinessModel, categoryId?: string, transportationMethod?: string) => {
    onSelectBusinessModel(businessModel, categoryId, transportationMethod);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="business-model-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🏭 Select Business Model</h2>
          <div className="header-actions">
            {currentUserRole === 'MASTER' && (
              <button 
                onClick={() => {
                  onClose();
                  if (onEditBusinessModel) {
                    // Pass null to indicate "create new"
                    onEditBusinessModel(null as any);
                  }
                }}
                className="create-model-btn"
                title="Create new business model"
              >
                ➕ Create New Business Model
              </button>
            )}
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">Loading business models...</div>
          ) : error ? (
            <div className="error">
              <p>{error}</p>
              <button onClick={loadBusinessModels} className="retry-btn">
                🔄 Retry
              </button>
            </div>
          ) : (
            <div className="business-models-grid">
              {businessModels.map((model) => (
                <div
                  key={model.id}
                  className="business-model-card"
                >
                  {currentUserRole === 'MASTER' && onEditBusinessModel && (
                    <button 
                      className="edit-card-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditBusinessModel(model);
                      }}
                      title="Edit Business Model"
                    >
                      ✏️ Edit
                    </button>
                  )}
                  
                  <div className="card-icon">{model.icon}</div>
                  <div className="card-content">
                    <h3 className="card-title">{model.name}</h3>
                    <p className="card-description">{model.description}</p>
                    
                    <div className="card-sections-column">
                      <div className="card-transportation">
                        <strong>Transportation:</strong>
                        <div className="transportation-tags">
                          {model.transportationMethods.map((method, index) => (
                            <span 
                              key={index} 
                              className="transportation-tag clickable-transport"
                              onClick={() => handleBusinessModelSelect(model, undefined, method)}
                              title={`View templates for ${method}`}
                            >
                              {method}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="card-categories">
                        <strong>Categories:</strong>
                        <div className="categories-list-column">
                          {model.categories.map((category) => (
                            <div
                              key={category.id}
                              className="category-item clickable-category"
                              onClick={() => handleBusinessModelSelect(model, category.id)}
                              title={`View templates for ${category.name}`}
                            >
                              <span className="category-icon">{category.icon}</span>
                              <span className="category-name">{category.name}</span>
                              {category.description && (
                                <span className="category-desc">{category.description}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessModelSelectionModal;

